import type { LoaderFunctionArgs, MetaFunction, SerializeFrom } from "@remix-run/node"
import {
  btnWrap,
  btnWrap2,
  centerLogoWrap,
  descriptionStyle,
  h2Style,
  landingContent,
  landingWrap,
  landingh1Style,
  masonryItem,
  masonryWrap,
  previewImageStyle,
  previewImgWrap,
} from "./index.css"
import { json, useLoaderData, useNavigation, Link as RemixLink } from "@remix-run/react"
import getSession, { parseJwt } from "~/server/functions"
import LinkButton from "~/components/LinkButton"
import ImageAndText from "~/components/ImageAndText"
import TextBlock from "~/components/TextBlock"
import Footer from "~/components/Footer"
import { Loader } from "~/components/Button"
import { mainWrap } from "~/components/ImageAndText/imageAndText.css"
import {
  articleBlurb,
  articleBodyStyle,
  articleDetails,
  articleSection,
  articleTitle,
  faqGrid,
  h3Style,
  imagePreviewBlog,
  niceTextEffect,
  shrinkingGrid,
  shrinkingGrid2,
  shrinkingGridItem,
  stepSectionStyle,
  testimonialStyle,
} from "./article.css"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import { golden } from "~/components/styles/theme.css"
import Link from "~/components/Link"
import StickyHeader from "~/components/StickyHeader"
import useClientLayoutEffect from "~/components/useClientLayoutEffect"
import { Section } from "~/components/articleComponents"

function shuffleArray(array: { url: string; alt: string }[]) {
  for (var i = array.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1))
    var temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }

  return array
}

const pages = [
  {
    title: "Try before you buy",
    slug: "/article/free-trial-ai-garden-design",
    description:
      "Rescape AI new offer: Generate one free image with Rescape AI and see why we're better than the competition",
    url: "/articles/free-trial-contemporary.webp",
  },
  {
    title: "Features of Contemporary Gardens",
    slug: "garden-design-guide/contemporary/features-of-contemporary-gardens",
    description:
      "Discover the key features of contemporary gardens, including minimalist layouts and modern materials.",
    url: "/guide/architectural-plants.webp",
  },
  {
    title: "Choosing Plants in Your US Hardiness Zone",
    slug: "article/choosing-plants-suitable-for-different-us-hardiness-zones",
    description:
      "Discover the top trees, shrubs, and perennials for your US hardiness zone. Find plants that thrive in your climate with expert tips and advice.",
    url: "/articles/achillea_millefolium.webp",
  },
  {
    title: "Climate Conscious Gardening",
    slug: "article/gardening-while-adapting-to-climate-change",
    description:
      "Discover how resilient planting, smart design and biodiversity can help gardens thrive in a changing climate.",
    url: "/articles/bee-collecting-pollen.webp",
  },
  {
    title: "Small Garden Ideas",
    slug: "/small-garden-ideas",
    description: "Small Garden Ideas That Make Every Inch Count.",
    url: "/articles/248d8f94-104f-48c0-88bd-4b36cea2c8213-cropped.webp",
  },
]

const masonryImgUrls = shuffleArray([
  {
    url: "landing/grid/2fd1277d-00c4-460a-ab30-a9901cf3e1251.webp",
    alt: "Tall, colourful tropical plants thoughtfully placed, creating a vibrant and playful appeal",
  },
  {
    url: "landing/grid/5cffdd1c-7ee5-4667-b39a-cb3681fad70f3.webp",
    alt: "Flowing organic flower bed showcasing an abundance of plant life.",
  },
  {
    url: "landing/grid/cozy-balcony-ai-generated.webp",
    alt: "A modern balcony has received its finishing touches to compliment the seating area. Bold green plantage creates a relaxing atmosphere.",
  },
  {
    url: "landing/grid/c20b1846-d4bd-442b-abf6-aa9f1074b9342.webp",
    alt: "Natural mossy ground covering and living walls bring a tired concreted area to life.",
  },
  {
    url: "landing/grid/f2d0575e-8e19-4002-8109-970f4b561c3e2.webp",
    alt: "A stunning wooden pergola draped with climbing plants creates a mystical seating area for entertaining.",
  },
  {
    url: "landing/grid/gen-2-1.webp",
    alt: "Contemporary borders of bold leaved plants surround a restful seating area.",
  },
  {
    url: "landing/grid/daf35847-b0df-43ff-b57d-6bd7d2d6cc692.webp",
    alt: "An inviting winding pathway leads to the door through a garden reminiscent of a wildflower meadow.",
  },
  {
    url: "landing/grid/gen-3-2.webp",
    alt: "Even the smallest patch of soil can be brought to life.",
  },
  {
    url: "landing/grid/e6595101-cc8e-45d4-8842-5f58a101182c1.webp",
    alt: "Making use of all of the empty space, here the model has chosen to fill space with a winding pathway through an abundance of plants.",
  },
  {
    url: "landing/grid/gen-5-4.webp",
    alt: "Borders in your garden provide a sense of protection, drawing attention to the tranquil space that helps melt away stress.",
  },
  {
    url: "landing/grid/f43bac96-1ae7-44df-a6e1-7d6d957a1c521.webp",
    alt: "Firepits can be a centerpiece of a garden landscape project.",
  },
  {
    url: "landing/grid/f7037165-88fc-40ec-8e42-5d5a0a0ca33d2.webp",
    alt: "An inviting front yard features a relaxed pathway that leads to the house, lined with thoughtfully arranged plants enhancing its beauty and charm.",
  },
  {
    url: "landing/grid/formal-garden-ai-generated.webp",
    alt: "A formal garden with straight lines and deliberate layout.",
  },
  {
    url: "landing/grid/gen-1-2.webp",
    alt: "A creative approach to small garden design that features small bushes and gravel flowerbeds that are easy to look after.",
  },
  {
    url: "landing/grid/gen-3-1.webp",
    alt: "A tiny patch of dirt can be made something quite beautiful with AI design.",
  },
  {
    url: "landing/grid/wildlife-friendly-natural-pond.webp",
    alt: "A stunning and huge wildlife friendly pond. adding immense value with benefits to your garden and your wellbeing with the frequent wild visitors who will be attending.",
  },
  {
    url: "landing/grid/92653ce3-ca32-4a42-a221-753e59dbe3431.webp",
    alt: "Stone walls and small patches of plants and flowers have created a captivating space.",
  },
  {
    url: "landing/grid/gen-4-1.webp",
    alt: "White roses, a formal fountain and meticulously constructed walls create an upscale landscape design.",
  },
  {
    url: "landing/grid/gen-4-2.webp",
    alt: "A polished AI landscape design. Curved flower beds with a variety of plans and and bushes among the cobbled patio.",
  },
  {
    url: "landing/grid/gen-4-4.webp",
    alt: "A Modern and high class patio area creating a sense of elegance and sophistication, perfect for entertaining guests or enjoying quiet evenings under the stars.",
  },
  {
    url: "landing/grid/gen-4-5.webp",
    alt: "Flowers packed among the patio area, a joyful and carefree design.",
  },
  {
    url: "landing/grid/gen-5-1.webp",
    alt: "Here, the AI garden design has improved the current garden borders, with curved flower beds, a natural beauty emerges that reflects the rural environment.",
  },
  {
    url: "landing/grid/gen-1-1.webp",
    alt: "Flowers, plants, clean grass, and a seating area tucked away within the flower border create order and comfort in this small garden.",
  },
  {
    url: "landing/grid/small-garden-ai-generated.webp",
    alt: "Front yard improvements. With minimal effort, some pebels and pots and plants to one corner of your yard, you can create a friendly space to welcome your guests.",
  },
  {
    url: "landing/grid/wildlife-friendly-natural-pond-2.webp",
    alt: "Our powerful model generates natural ponds too. Here a section of the garden has been converted to a wildlife friendly pond.",
  },
  {
    url: "landing/grid/wildlife-garden-with-pond-ai-generated.webp",
    alt: "Who needs grass? Here the entire garden has been transformed to accomodate a pond surrounded by sleek concrete paving slabs that yells prosperity.",
  },
])

export const meta: MetaFunction = () => {
  return [
    { title: "AI Garden Design & Landscape Inspiration | Find Your Style" },
    {
      name: "description",
      content:
        "Utilising cutting edge AI technology for garden and landscape design, Rescape AI supercharges your research and creatively suggests professional designs.",
    },
    {
      property: "og:title",
      content: "Rescape AI - Generate ideas for outdoors, instantly",
    },
    {
      property: "og:description",
      content:
        "Utilise cutting edge AI to generate garden designs tailored to your own outdoor space.",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Rescape AI",
        applicationCategory: "BrowserApplication",
        operatingSystem: "Web",
        description:
          "Advanced AI gardening and landscaping tool that generates professional design ideas.",
        url: "https://rescapeai.com",
        softwareVersion: "1.0",
        permissions: "Camera",
        offers: [
          {
            "@type": "Offer",
            name: "Starter",
            description: "125 images",
            price: "8.99",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://rescapeai.com/purchase",
          },
          {
            "@type": "Offer",
            name: "Standard",
            description: "250 images",
            price: "12.99",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://rescapeai.com/purchase",
          },
          {
            "@type": "Offer",
            name: "Pro",
            description: "250 images per month",
            price: "9.99",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://rescapeai.com/purchase",
          },
          {
            "@type": "Offer",
            name: "Business",
            description: "500 images per month (HD)",
            price: "19.99",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://rescapeai.com/purchase",
          },
        ],
        creator: {
          "@type": "Organization",
          name: "Rescape AI",
          url: "https://rescapeai.com",
        },
        reviewRating: {
          "@type": "Rating",
          ratingValue: 5,
        },
      },
    },
  ]
}

export default function Index() {
  const { redirect } = useLoaderData<SerializeFrom<typeof loader>>()

  const navigation = useNavigation()

  let btnUrl, btnTitle

  switch (redirect) {
    case "generate":
      btnUrl = "/generate"
      btnTitle = "Generate"
      break
    case "login":
      btnUrl = "/hello/login"
      btnTitle = "Login"
      break
    case "signup":
      btnUrl = "/hello/login"
      btnTitle = "Begin"
      break
    default:
      btnUrl = "/hello/signup"
      btnTitle = "Begin"
      break
  }

  return (
    <>
      <div className={landingWrap}>
        <div className={landingContent}>
          <div className={centerLogoWrap}>
            <Logo width="105px" />
          </div>
          <h1 className={landingh1Style}>AI Garden Design: Generate Ideas Instantly</h1>
          <p className={descriptionStyle}>
            Generate unique professional{" "}
            <Link href="/ai-landscape-design">AI landscape design</Link> and{" "}
            <Link href="/ai-garden-design">AI garden design</Link> with Rescape AI.
          </p>
          <div className={btnWrap}>
            <LinkButton url={btnUrl} shouldPrefetch>
              {navigation.state !== "idle" ? <Loader /> : btnTitle}
            </LinkButton>
          </div>
        </div>
        <div className={previewImgWrap}>
          <img
            src="/landing/contemporary-garden-designed-with-ai.webp"
            alt="Minimalist open garden space with beautiful seating area | Designed by Rescape AI"
            className={previewImageStyle}
          />
        </div>
      </div>
      <StickyHeader
        status={redirect === "login" || redirect === "generate" ? "Login" : "SignUp"}
      />
      <div className={page} style={{ paddingTop: "64px" }}>
        <p className={`${h2Style}`} style={{ marginTop: 0 }}>
          How does it work?
        </p>
        <ImageAndText
          items={[
            {
              imgUrl: "/landing/ai-generated-garden-before_thumb.webp",
              imgAlt: "A garden in need of landscape design.",
              imgTitle: "A garden before Rescape AI...",
              text: "Photograph your garden.",
            },
            {
              imgUrl: "/landing/ai-generated-garden-variation-one_thumb.webp",
              imgAlt:
                "AI garden design featuring a beautiful garden with colorful potted flowers and a cozy seating area.",
              imgTitle: "Diverse potted flowers and seating area.",
              text: "And generate garden design ideas.",
            },
            {
              imgUrl: "/landing/ai-generated-garden-variation-two_thumb.webp",
              imgAlt:
                "Contemporary garden design with attractive decking and a modern living wall.",
              imgTitle: "Contemporary AI generated garden..",
              text: "Of many styles.",
            },
            {
              imgUrl: "/landing/garden-before-being-designed-with-ai_thumb.webp",
              imgAlt: "A garden without a clear style or direction.",
              imgTitle: "A garden before Rescape AI.",
              text: "From a lack of direction.",
            },
            {
              imgUrl: "/landing/contemporary-garden-designed-with-ai_thumb.webp",
              imgAlt:
                "AI landscape design with Rescape AI generates an alluring space with an abundance of character with the paved pathway and vibrant encompassing plants.",
              imgTitle: "Your garden reimagined with AI garden design.",
              text: "To professional design and ideas.",
            },
          ]}
        />
        <div className={btnWrap2}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Test it free
          </LinkButton>
        </div>
        <section className={articleSection}>
          <div className={stepSectionStyle}>
            <h2 className={h2Style}>From our Blog and Garden Design Guide</h2>
            <p className={articleBodyStyle} style={{ textAlign: "center" }}>
              Read more about designing your garden on the{" "}
              <RemixLink
                title="Rescape AI: Garden Design Guide"
                to="/garden-design-guide"
              >
                Garden Design Guide
              </RemixLink>
            </p>
          </div>
          <ul className={shrinkingGrid} style={{ marginTop: `${golden}rem` }}>
            {pages.map((article) => {
              return (
                <li key={article.slug} className={shrinkingGridItem}>
                  <RemixLink
                    title={article.title}
                    to={article.slug}
                    style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                  >
                    <img src={article.url} className={imagePreviewBlog} />
                    <span className={articleDetails}>
                      <h2
                        className={articleTitle}
                        style={{
                          textTransform: "capitalize",
                        }}
                      >
                        {article.title}
                      </h2>
                      <p className={articleBlurb}>{article.description}</p>
                    </span>
                  </RemixLink>
                </li>
              )
            })}
          </ul>
        </section>
        <div className={btnWrap} style={{ marginTop: `${golden}rem` }}>
          <LinkButton url="/ai-landscape-design">AI Landscape Design</LinkButton>
        </div>
        <section className={articleSection}>
          <h3
            className={`${h3Style} ${niceTextEffect}`}
            id="user-testimonials"
            style={{
              textAlign: "center",
              fontSize: "2.6rem",
            }}
          >
            User Testimonials and Reviews
          </h3>
          <blockquote className={testimonialStyle}>
            "I'm over the moon with it - It's absolutely fantastic and is so exciting to
            see what it comes up with. Love it. " — Louise
          </blockquote>
          <blockquote className={testimonialStyle}>
            "Wow! It's amazing! And there are so many styles and choices! " — Jenny
          </blockquote>
        </section>
        <div className={masonryWrap}>
          {masonryImgUrls.map(({ url, alt }) => (
            <img
              key={url}
              src={url}
              alt={alt}
              title="AI Generated Designs"
              className={masonryItem}
              style={{ width: "100%", height: "auto", borderRadius: "6px" }}
              loading="lazy"
            />
          ))}
        </div>
        <div className={mainWrap}>
          <div className={faqGrid}>
            <div>
              <TextBlock
                title="Ai garden design and yard design in seconds"
                paragraphs={[
                  `At Rescape AI you can generate images based on your outdoor space in seconds. This is why thousands of people have already used us to create realistic variations of their outdoor space`,
                  <Link href="/garden-design-guide/contemporary/features-of-contemporary-gardens">
                    {" "}
                    Read about popular styles
                  </Link>,
                ]}
              />
            </div>
            <div>
              <TextBlock
                title="Affordable Garden Ideas"
                paragraphs={[
                  `Join thousands of people using Rescape AI. Purchase credits from as little as $8.99. (Images cost 1 or 2 credits based on version.)`,
                ]}
              />
            </div>
            <div>
              <TextBlock
                title="Creative inspiration"
                paragraphs={[
                  `AI garden design gives you the ability to generate variations of your actual outdoor space. The tool accepts an image of your garden or landscaping project and based on a selections and text prompts will redesign that outdoor space in seconds.`,
                ]}
              />
            </div>
          </div>
        </div>
        <div className={faqGrid}>
          <div>
            <TextBlock
              title="High quality, landscaping ideas, in seconds."
              paragraphs={[
                `Rescape AI's generator creates appealing landscape design for you. In seconds 
          you can utilise it experiment with new styles for your garden.`,
              ]}
            />
          </div>
          <div>
            <TextBlock
              title="Outdoor space garden planner."
              paragraphs={[
                `Design your garden with cutting edge AI. The Rescape AI garden generator can be
        used as a garden planner or a tool to design your garden. Take inspiration from
        the generated images to style in ways that only a professional could imagine.`,
              ]}
            />
          </div>
          <div>
            <TextBlock
              title="Unveiling the Beauty of Your Outdoor Space!"
              paragraphs={[
                `Our AI garden generator not only crafts `,
                <Link
                  href="/article/how-to-use-ai-for-landscape-design#examples"
                  key="link"
                >
                  high-quality designs
                </Link>,
                ` in seconds but also
        acts as a perpetual source of inspiration. Experiment with styles only
        professionals could imagine, using the same photo repeatedly for endless
        possibilities.`,
              ]}
            />
          </div>
        </div>
      </div>
      <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
        <LinkButton url={"/hello/signup"} shouldPrefetch>
          {navigation.state !== "idle" ? <Loader /> : "Join Now"}
        </LinkButton>
      </div>
      <Footer />
    </>
  )
}

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request)

  if (session) {
    const parsed = parseJwt(session.accessToken)
    const millisTilExpired = parsed.exp * 1000

    if (millisTilExpired > Date.now()) {
      return json({ redirect: "generate" })
    } else {
      return json({ redirect: "login" })
    }
  }
  return json({ redirect: "signup" })
}
